import { useCallback, useMemo, useRef, useState } from "react";

import { useDisclosure } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import { Page } from "shared/components/Page";
import { ScannableInput, ScannableInputHandle } from "shared/components/ScannableInput";
import { IntlMessageId } from "shared/lang";
import { Form } from "ui/Form/Form";
import { RemovableListItem } from "ui/RemovableListItem/RemovableListItem";
import { HeaderS } from "ui/Typography/Typography";

import { GoToAssignShelvesButton } from "../../components/GoToAssignShelvesButton";
import { ScanContainerSkipConfirmationModal } from "../../components/ScanContainerSkipConfirmationModal";
import { SkipScanningButton } from "../../components/SkipScanningButton";
import { usePickingActions } from "../../hooks/usePickingActions";
import { usePickingStore } from "../../stores/usePickingStore";

const isContainerIdValid = (input: string | undefined) => {
  if (input) {
    return /^[a-z0-9]{5,8}$/i.test(input);
  }

  return false;
};

export function ScanContainersPage() {
  const intl = useIntl();

  const { goToAssignShelves } = usePickingActions();
  const containersIds = usePickingStore((state) => state.containersIds);
  const endPickingLoading = usePickingStore((state) => state.endPickingPhase === "loading");

  const [errorTextId, setErrorTextId] = useState<IntlMessageId | undefined>(undefined);
  const refInput = useRef<ScannableInputHandle | null>(null);

  const {
    isOpen: isScanContainerSkipConfirmationModalOpen,
    onOpen: openScanContainerSkipConfirmationModal,
    onClose: closeScanContainerSkipConfirmationModal,
  } = useDisclosure();

  const onClickSkipButton = () => {
    openScanContainerSkipConfirmationModal();
  };

  const addContainer = useCallback(
    (newContainerId: string) => {
      if (newContainerId === "") {
        return false;
      }
      if (!isContainerIdValid(newContainerId)) {
        setErrorTextId("pages.picking.scan-container.input-error-invalid-format");
        return false;
      }
      if (containersIds.find((id) => id === newContainerId)) {
        setErrorTextId("pages.picking.scan-container.input-error-already-scanned");
        return false;
      }
      usePickingStore.getState().addContainer(newContainerId);
      setErrorTextId(undefined);
      refInput?.current?.resetValue();
      return true;
    },
    [containersIds],
  );

  const onConfirmSkip = () => {
    usePickingStore.getState().resetContainers();
    goToAssignShelves();
  };

  const onFormSubmit = useCallback(
    (e?: React.FormEvent<HTMLFormElement>) => {
      e?.preventDefault();
      const currentValue = refInput.current?.value;
      if (!currentValue) return true;
      usePickingStore.getState().setInputMethod("MANUAL");
      return addContainer(currentValue);
    },
    [addContainer],
  );

  const endScanContainersButton = useMemo(() => {
    const isButtonDisabled = !containersIds.length || !!errorTextId;
    return (
      <GoToAssignShelvesButton
        mt="s300"
        isButtonDisabled={isButtonDisabled}
        onValidateContinue={onFormSubmit}
      />
    );
  }, [containersIds.length, errorTextId, onFormSubmit]);

  return (
    <Page isCentered scrollBehavior="auto">
      <HeaderS textAlign="center">
        <FormattedMessage id="pages.picking.scan-container.title" />
      </HeaderS>
      <Form onSubmit={onFormSubmit} alignItems="center" w="100%">
        {containersIds.map((containerId) => (
          <RemovableListItem
            key={containerId}
            title={containerId.slice(-5)}
            onClickRemove={() => {
              usePickingStore.getState().removeContainer(containerId);
              refInput.current?.focus();
            }}
          />
        ))}
        <ScannableInput
          ref={refInput}
          inputName="container-id"
          error={errorTextId ? intl.formatMessage({ id: errorTextId }) : undefined}
          placeholder={intl.formatMessage({
            id: "pages.picking.scan-container.input-placeholder",
          })}
          onScan={(scannedValue) => {
            usePickingStore.getState().setInputMethod("SCAN");
            addContainer(scannedValue);
          }}
          resetErrorTextId={() => setErrorTextId(undefined)}
          isAutoFocus
          preventKeyboard
        />
        {endScanContainersButton}
      </Form>
      <SkipScanningButton onClickSkipButton={onClickSkipButton} isDisabled={endPickingLoading} />
      {isScanContainerSkipConfirmationModalOpen && (
        <ScanContainerSkipConfirmationModal
          onClickYes={onConfirmSkip}
          onClickNo={closeScanContainerSkipConfirmationModal}
        />
      )}
    </Page>
  );
}
